// client/src/hooks/useAnamneses.js
// Mutations do fluxo de preenchimento (painel web) — leitura/histórico
// continua em useAnamnesesHistorico.js (separação já usada em useContratos.js
// pra queries vs. useAssinarContrato/useCancelarContrato).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const BASE_ROUTE = 'anamneses';
const QUERY_KEY = 'anamnesesHistorico';

const invalidar = (queryClient, id) => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    if (id) queryClient.invalidateQueries({ queryKey: [QUERY_KEY, id] });
};

// Cria a ficha imediatamente em status 'draft' — sem exigir respostas nem
// assinatura ainda, mesmo que o template exija assinatura (só é cobrada na
// finalização, via useUpdateAnamnese). Isso dá um _id real pra anexar fotos.
export const useCreateAnamneseDraft = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: ({ clienteId, templateId, servicoId }) =>
            apiClient.post(`/${BASE_ROUTE}`, { clienteId, templateId, servicoId, rascunho: true }).then((r) => r.data.data),
        onSuccess: () => invalidar(queryClient),
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao criar rascunho de anamnese.', 'danger'),
    });
};

// PUT /anamneses/:id — só funciona enquanto status==='draft'. Usado tanto
// pra salvar respostas incrementalmente (sem nenhuma flag de finalização)
// quanto pra finalizar: envie `assinaturaCliente` (assina agora),
// `gerarLinkAssinatura:true` (aguarda assinatura remota por link) ou
// `finalizar:true` (só quando o template não exige assinatura).
export const useUpdateAnamnese = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: ({ id, ...payload }) => apiClient.put(`/${BASE_ROUTE}/${id}`, payload).then((r) => r.data.data),
        onSuccess: (_data, variables) => invalidar(queryClient, variables.id),
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao salvar anamnese.', 'danger'),
    });
};

// POST /anamneses/:id/galeria — upload síncrono direto pra S3, só permitido
// enquanto a ficha está em rascunho. `itemId` identifica a pergunta do tipo
// Galeria à qual a foto pertence (uma ficha pode ter mais de um campo Galeria).
export const useUploadGaleriaAnamnese = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: ({ id, itemId, file }) => {
            const formData = new FormData();
            formData.append('itemId', itemId);
            formData.append('file', file);
            return apiClient.post(`/${BASE_ROUTE}/${id}/galeria`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }).then((r) => r.data.data);
        },
        onSuccess: (_data, variables) => invalidar(queryClient, variables.id),
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar foto.', 'danger'),
    });
};

// POST /anamneses/:id/gerar-link-assinatura — só quando status==='awaiting_signature'
// (ficha já finalizada via useUpdateAnamnese com gerarLinkAssinatura:true).
// Retorna a URL pronta pra copiar/enviar ao cliente.
export const useGerarLinkAssinatura = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: (id) => apiClient.post(`/${BASE_ROUTE}/${id}/gerar-link-assinatura`).then((r) => r.data.data),
        onSuccess: (_data, id) => invalidar(queryClient, id),
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao gerar link de assinatura.', 'danger'),
    });
};

export const useDeleteAnamnese = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    return useMutation({
        mutationFn: (id) => apiClient.delete(`/${BASE_ROUTE}/${id}`),
        onSuccess: () => {
            invalidar(queryClient);
            setAlert('Ficha de anamnese removida com sucesso.', 'success');
        },
        onError: (err) => setAlert(err.response?.data?.error || err.response?.data?.message || 'Erro ao remover ficha.', 'danger'),
    });
};
